import { _ } from "libs";
import React, { useEffect, useState } from "react";
import withRedux from "../../withRedux";
import Modal from "../modal";
import { closeInvitePopup, getChampionshipInvites, addChampionshipInvite } from "../../actions";
import { RootState } from "../../reducers";
import { Championship, ChampionshipInvitePayload, ChampionshipInviteStatus, InvitePlayer } from "../../types";
import { InvisibleInfiniteLoader } from "../infiniteLoader";
import CopyUrlButton from "../../libs/CopyToClipboard";
import PlayerItemMedium from "../items/player/medium";
import DefaultPlayerMeta from "../items/player/defaultMeta";
import cx from "classnames";


function InviteModal({
	show = false,
	championship = null,
	presetSearchTerm = null,
	inviteList = [],
	presetCurrentPage = 0,
}: {
	show: boolean;
	championship: Championship;
	presetSearchTerm: string;
	inviteList: any[];
	presetCurrentPage: number;
}) {
	const [invites, setInvites] = useState(inviteList as InvitePlayer[]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [shareUrl, setShareUrl] = useState(null as any);
	const [inviteQuery, setInviteQuery] = useState({
		currentPage: presetCurrentPage,
		term: presetSearchTerm,
	});

	const PAGE_SIZE = 20;

	useEffect(() => {
		let unmounted = false;
		if (!championship || unmounted) {
			return;
		}

		loadInvites(championship.uuid, inviteQuery.term, inviteQuery.currentPage, PAGE_SIZE).then((res) =>
			setShareUrl(res.shareUrl)
		);

		return () => {
			if (searchTimeout) {
				clearTimeout(searchTimeout);
			}
			unmounted = true;
		};
	}, [championship, inviteQuery]);

	const loadInvites = async (
		championshipId: string,
		term: string = null,
		pageCurrent: number = 0,
		pageLimit: number = 10
	): Promise<ChampionshipInvitePayload> => {
		try {
			setLoading(true);
			const res = await getChampionshipInvites(championshipId, term, pageCurrent, pageLimit);
			const data = res.data;
			const players = data.players;
			setInvites([...invites, ...players]);
			setHasMore(players.length >= PAGE_SIZE);
			return data;
		} catch (error) {
		} finally {
			setLoading(false);
		}
	};

	const close = () => {
		closeInvitePopup();
		setInvites([]);
		setInviteQuery({ term: null, currentPage: 0 });
	};

	const onViewChangeDown = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		if (!championship) {
			return;
		}
		const shouldLoad = inView && invites.length > 0 && hasMore;

		if (shouldLoad) {
			setInviteQuery({ ...inviteQuery, currentPage: inviteQuery.currentPage + 1 });
		}
	};

	const invitePlayer = async (player: InvitePlayer): Promise<string | null> => {
		try {
			await addChampionshipInvite(championship.uuid, player.playerId);
			return ChampionshipInviteStatus.INVITED;
		} catch (error) {
			return player.inviteStatus;
		}
	};

	function Invitee({ invitee }: { invitee: InvitePlayer }) {
		const [inviteState, setInviteState] = useState(invitee.inviteStatus as any);

		return (
			<>
				<PlayerItemMedium name={invitee.fullName} meta={DefaultPlayerMeta(invitee)} photoUrl={invitee.photoUrl} />
				{!inviteState ? (
					<div
						className="badgeButton"
						onClick={() => {
							invitePlayer(invitee).then((result) => setInviteState(result));
						}}
					>
						{_("championship.invitation.invite")}
					</div>
				) : (
					<div className={cx("badgeButton", { "disabled plainGrey": inviteState === "joined" }, { "disabled grey": inviteState === "invited" } )}>{_("championship.invitation." + inviteState)}</div>
				)}
			</>
		);
	}

	const renderInvitee = (invitee: InvitePlayer, key: number) => {
		return (
			<div className="cell flex" key={`${invitee.playerId}_${key}_invite_${key}`}>
				<Invitee invitee={invitee} />
			</div>
		);
	};

	let searchTimeout;

	const onTermChange = (e) => {
		const value: string = e?.target?.value || "";
		if (value.length <= 3 && value.length > 0 && value != inviteQuery.term) {
			return;
		}

		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		searchTimeout = setTimeout(() => {
			setInvites([]);
			setInviteQuery({
				term: value,
				currentPage: 0,
			});
		}, 2000);
	};

	if (show === false) {
		return null;
	}

	if (!championship) {
		return null;
	}

	return (
		<Modal title={_("championship.invite.title")} onCloseClicked={close}>
			<div className="inviteModalContent">
				<div className="field searchField searchFieldWithIcon">
					<div className="input">
						<input type="text" placeholder={_("form.searchPlayer.placeholder")} onChange={onTermChange} disabled={loading} />
					</div>
				</div>
				<div className="table looseSpacing pb-4">{invites.map(renderInvitee)}</div>
				{hasMore ? <InvisibleInfiniteLoader onViewChange={onViewChangeDown} loading={loading} /> : null}
				{shareUrl && (
					<div className="shareLink">
						<h4>{_("championship.invitation.shareWithFriends")}</h4>
						<CopyUrlButton text={shareUrl} />
					</div>
				)}
			</div>
		</Modal>
	);
}

function mapStateToProps({ inviteModal: { show, championship } }: RootState) {
	return { show, championship, presetSearchTerm: null, inviteList: [], presetCurrentPage: 0 };
}

export default withRedux({}, mapStateToProps)(InviteModal);
